import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Paper, Tab, Tabs, TextField, Typography, Alert, Snackbar, Stack,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { gamesApi } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import './MatkaScreen.css';

const BET_TYPES = [
  { key: 'OPEN_SINGLE', label: 'Open Single', hint: '0-9', payout: 9, maxLen: 1 },
  { key: 'CLOSE_SINGLE', label: 'Close Single', hint: '0-9', payout: 9, maxLen: 1 },
  { key: 'JODI', label: 'Jodi', hint: '00-99', payout: 90, maxLen: 2 },
  { key: 'OPEN_PANNA', label: 'Open Panna', hint: '000-999', payout: 140, maxLen: 3 },
  { key: 'CLOSE_PANNA', label: 'Close Panna', hint: '000-999', payout: 140, maxLen: 3 },
];

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function MatkaScreen({ user, onBack, onWalletUpdate }) {
  const [data, setData] = useState(null);
  const [marketIndex, setMarketIndex] = useState(0);
  const [betType, setBetType] = useState('OPEN_SINGLE');
  const [betNumber, setBetNumber] = useState('');
  const [amount, setAmount] = useState('50');
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
    } catch { /* ignore */ }
  }, [onWalletUpdate]);

  const load = useCallback(() => {
    gamesApi.matkaMarkets().then(setData).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);

  const market = data?.markets?.[marketIndex];
  const draw = market?.currentDraw;
  const selectedType = BET_TYPES.find((t) => t.key === betType);
  const payoutPreview = selectedType && amount ? Number((Number(amount) * selectedType.payout).toFixed(2)) : 0;

  const placeBet = async () => {
    if (!draw?.bettingOpen) { setToast('Betting closed'); return; }
    const stake = Number(amount);
    if (!Number.isFinite(stake) || stake <= 0 || stake > walletBalance) {
      setToast('Invalid amount or insufficient balance'); return;
    }
    if (!betNumber.trim()) { setToast('Enter your number'); return; }

    setLoading(true);
    try {
      const result = await gamesApi.matkaBet({
        drawId: draw.id, betType, betNumber: betNumber.trim(), amount: stake,
      });
      setToast(`Bet placed! Win up to ₹${result.payoutOnWin}`);
      setBetNumber('');
      await refreshUser();
      load();
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="matka-root">
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} />

      <Box className="matka-header">
        <IconButton size="small" onClick={onBack} sx={{ color: '#fff' }}>
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="matka-title">Matka / Satta</Typography>
        <Chip size="small" label={`₹${walletBalance.toFixed(0)}`} sx={{ color: '#fff' }} />
      </Box>

      <Tabs value={marketIndex} onChange={(_, v) => setMarketIndex(v)} variant="scrollable" scrollButtons="auto" className="matka-market-tabs">
        {(data?.markets ?? []).map((m) => <Tab key={m.id} label={m.name} />)}
      </Tabs>

      {market && draw ? (
        <>
          <Paper className="matka-result-card" elevation={0}>
            <Typography className="draw-label">Draw #{draw.drawNo.slice(-10)}</Typography>
            {draw.bettingOpen ? (
              <>
                <Typography className="timer-big">{formatTime(draw.timeLeft)}</Typography>
                <Typography className="timer-sub">Betting closes in</Typography>
              </>
            ) : draw.jodi ? (
              <Box className="result-grid">
                <Box className="result-cell">
                  <Typography variant="caption">Open</Typography>
                  <Typography className="result-val">{draw.openDigit}</Typography>
                  <Typography variant="caption">{draw.openPanna}</Typography>
                </Box>
                <Box className="result-cell highlight">
                  <Typography variant="caption">Jodi</Typography>
                  <Typography className="result-val">{draw.jodi}</Typography>
                </Box>
                <Box className="result-cell">
                  <Typography variant="caption">Close</Typography>
                  <Typography className="result-val">{draw.closeDigit}</Typography>
                  <Typography variant="caption">{draw.closePanna}</Typography>
                </Box>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>Result declaring… next draw soon</Alert>
            )}
          </Paper>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ mb: 1 }}>
            <Tab label="Place Bet" />
            <Tab label="My Bets" />
            <Tab label="History" />
          </Tabs>

          {tab === 0 && (
            <Paper className="matka-bet-panel" elevation={0}>
              <Typography className="panel-title">Select bet type</Typography>
              <Box className="bet-type-grid">
                {BET_TYPES.map((t) => (
                  <Button key={t.key} size="small" variant={betType === t.key ? 'contained' : 'outlined'}
                    onClick={() => { setBetType(t.key); setBetNumber(''); }}>
                    {t.label} ({t.payout}x)
                  </Button>
                ))}
              </Box>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <TextField size="small" label={`Number (${selectedType?.hint})`} value={betNumber}
                  onChange={(e) => setBetNumber(e.target.value.replace(/\D/g, '').slice(0, selectedType?.maxLen ?? 3))}
                  fullWidth inputProps={{ inputMode: 'numeric' }} />
                <TextField size="small" label="Amount (₹)" type="number" value={amount}
                  onChange={(e) => setAmount(e.target.value)} fullWidth />
                <Typography variant="body2" color="text.secondary">If win: ₹{payoutPreview} ({selectedType?.payout}x)</Typography>
                <Button variant="contained" size="large" fullWidth disabled={loading || !draw.bettingOpen} onClick={placeBet} sx={{ fontWeight: 800 }}>
                  {draw.bettingOpen ? `Bet ₹${Number(amount || 0)}` : 'Betting Closed'}
                </Button>
              </Stack>
            </Paper>
          )}

          {tab === 1 && (
            <Paper className="matka-list-panel" elevation={0}>
              {market.myBets?.length ? market.myBets.map((b) => (
                <Box key={b.id} className="matka-list-row">
                  <Box>
                    <Typography fontWeight={700}>{b.betType.replace(/_/g, ' ')}</Typography>
                    <Typography variant="caption">#{b.betNumber} · ₹{b.amount}</Typography>
                  </Box>
                  <Chip size="small" label={b.status === 'WON' ? `+₹${b.payout}` : b.status}
                    color={b.status === 'WON' ? 'success' : b.status === 'LOST' ? 'error' : 'default'} />
                </Box>
              )) : <Typography color="text.secondary" sx={{ p: 2 }}>No bets this draw</Typography>}
            </Paper>
          )}

          {tab === 2 && (
            <Paper className="matka-list-panel" elevation={0}>
              {market.history?.map((h) => (
                <Box key={h.drawNo} className="matka-list-row">
                  <Typography variant="caption">{h.drawNo.slice(-8)}</Typography>
                  <Typography fontWeight={700}>{h.jodi}</Typography>
                  <Typography variant="caption">O:{h.openDigit}({h.openPanna}) C:{h.closeDigit}({h.closePanna})</Typography>
                </Box>
              ))}
            </Paper>
          )}
        </>
      ) : (
        <Typography sx={{ p: 3, textAlign: 'center', color: '#fff' }}>Loading markets…</Typography>
      )}
    </Box>
  );
}

export default MatkaScreen;
